import { useEffect, useRef, useState, useCallback } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const steps = [
  {
    title: 'Lead with artificial intelligence',
    description:
      'AI shapes the build from the first line, not bolted on after. We start from how your business works, then engineer the system around it. Every decision is intelligence-led.',
    image:
      'https://readdy.ai/api/search-image?query=Abstract%20artificial%20intelligence%20visualization%2C%20glowing%20neural%20network%20nodes%20on%20deep%20dark%20background%2C%20aurora%20green%20and%20amber%20light%20connections%2C%20minimal%20clean%20tech%20aesthetic%2C%20cinematic%20lighting%2C%20no%20text&width=900&height=1100&seq=build-step-1-ai-lead&orientation=portrait',
    accent: 'bg-primary-500',
    accentText: 'text-primary-400',
  },
  {
    title: 'Build to fit',
    description:
      'Software shaped to your operation, not a product you bend to. Every system is engineered to match your workflow precisely.&nbsp;',
    image:
      'https://readdy.ai/api/search-image?query=Modern%20software%20architecture%20blueprint%20visualization%2C%20abstract%20geometric%20shapes%20and%20flowing%20lines%20on%20dark%20background%2C%20subtle%20green%20and%20warm%20amber%20accents%2C%20minimal%20tech%20aesthetic%2C%20cinematic%20lighting%2C%20no%20text&width=900&height=1100&seq=build-step-2-custom&orientation=portrait',
    accent: 'bg-primary-500',
    accentText: 'text-primary-400',
  },
  {
    title: 'Always connected',
    description:
      'Every part shares the same data and the same system. One unified stack that talks to itself so you do not have to patch it together.',
    image:
      'https://readdy.ai/api/search-image?query=Abstract%20connected%20network%20system%20visualization%2C%20interwoven%20data%20streams%20and%20nodes%20on%20deep%20dark%20background%2C%20subtle%20green%20glow%20and%20warm%20amber%20highlights%2C%20minimal%20clean%20tech%20aesthetic%2C%20cinematic%20lighting%2C%20no%20text&width=900&height=1100&seq=build-step-3-connected&orientation=portrait',
    accent: 'bg-primary-500',
    accentText: 'text-primary-400',
  },
];

export default function HowWeBuildSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const rightColumnRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const viewportCenter = window.innerHeight / 2;

    slideRefs.current.forEach((slide, index) => {
      if (!slide) return;
      const rect = slide.getBoundingClientRect();
      const slideCenter = rect.top + rect.height / 2;
      if (Math.abs(slideCenter - viewportCenter) < rect.height / 2) {
        setActiveIndex(index);
      }
    });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.2 });

  return (
    <section
      id="how-we-build"
      ref={sectionRef}
      className="relative bg-background-950"
    >
      {/* Section header */}
      <div
        ref={headerRef}
        className={`w-full max-w-6xl mx-auto px-4 md:px-6 pt-16 md:pt-24 pb-12 transition-all duration-1000 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight max-w-4xl">
          <span className="text-primary-400">AI at the front</span>
          <br />
          not at the end
        </h2>
        <p className="mt-6 text-white/50 text-base md:text-lg leading-relaxed max-w-3xl">
          We put AI at the front of the build, not at the end. Artificial intelligence comes first, running through the whole system instead of being added on. We start from how your business works, then engineer the system around it.
        </p>
      </div>

      {/* Mobile: stacked cards — text + image together per step */}
      <div className="lg:hidden w-full max-w-2xl mx-auto px-4 md:px-6 space-y-12 pb-16">
        {steps.map((step, index) => (
          <div key={index} className="space-y-6">
            {/* Step indicator + text */}
            <div>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold text-white leading-tight mb-3">
                {step.title}
              </h3>
              <p className="text-white/50 text-base leading-relaxed">
                {step.description}
              </p>
              <a
                href="#what-we-build"
                className="inline-flex items-center gap-2 mt-5 text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors duration-200 cursor-pointer group"
              >
                Discover capabilities
                <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-0.5 transition-transform duration-200">
                  <i className="ri-arrow-right-line" />
                </span>
              </a>
            </div>
            {/* Image */}
            <div className="relative w-full aspect-[4/5] max-h-[500px] rounded-2xl overflow-hidden">
              <img
                src={step.image}
                alt={step.title}
                className="w-full h-full object-cover object-top animate-breathe-image"
                width={900}
                height={1100}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent animate-glow-pulse" />
              <div className="absolute bottom-6 left-6 right-6">
                <h4 className="font-heading text-lg font-semibold text-white">
                  {step.title}
                </h4>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: sticky scroll area */}
      <div className="hidden lg:block relative">
        <div className="flex flex-row">
          {/* Left: sticky text panel */}
          <div className="w-[45%] sticky top-0 h-screen flex items-center px-6">
            <div className="max-w-md">
              {/* Step indicator */}
              <div className="flex items-center gap-4 mb-8">
                {steps.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      slideRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                    className={`h-1 rounded-full transition-all duration-500 cursor-pointer ${
                      i === activeIndex
                        ? 'w-12 bg-primary-500'
                        : 'w-6 bg-white/20 hover:bg-white/40'
                    }`}
                    aria-label={`Go to step ${i + 1}`}
                  />
                ))}
              </div>

              {/* Step content */}
              <div className="relative min-h-[280px]">
                {steps.map((step, i) => (
                  <div
                    key={i}
                    className={`transition-all duration-700 ${
                      i === activeIndex
                        ? 'opacity-100 translate-y-0 relative'
                        : 'opacity-0 translate-y-4 absolute inset-0 pointer-events-none'
                    }`}
                  >
                    <span className={`inline-block px-4 py-1.5 rounded-md text-sm font-semibold text-background-950 ${step.accent} mb-6`}>
                      {step.title}
                    </span>
                    <p className="text-white/50 text-base leading-relaxed">
                      {step.description}
                    </p>
                    <a
                      href="#what-we-build"
                      className="inline-flex items-center gap-2 mt-6 text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors duration-200 cursor-pointer group"
                    >
                      Discover capabilities
                      <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-0.5 transition-transform duration-200">
                        <i className="ri-arrow-right-line" />
                      </span>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: scrolling image panels */}
          <div className="w-[55%]" ref={rightColumnRef}>
            {steps.map((step, index) => (
              <div
                key={index}
                ref={(el) => { slideRefs.current[index] = el; }}
                className="min-h-[85vh] flex items-center px-6 py-8"
              >
                <div className="relative w-full aspect-[4/5] max-h-[700px] rounded-2xl overflow-hidden group">
                  <img
                    src={step.image}
                    alt={step.title}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105 animate-breathe-image group-hover:[animation-play-state:paused]"
                    width={900}
                    height={1100}
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent animate-glow-pulse" />
                  {/* Bottom label */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <h4 className="font-heading text-xl font-semibold text-white">
                      {step.title}
                    </h4>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}